<template>
  <div class="ph-topnav">
    <div class="ph-topnav__inner">
      <!-- Mobile menu trigger -->
      <div ref="mobileMenuRef" class="ph-topnav__mobile-menu">
        <button
          class="ph-topnav__icon-btn ph-topnav__hamburger"
          :aria-label="$t('nav.menu')"
          @click.stop="mobileMenuOpen = !mobileMenuOpen"
        >
          <PhIcon name="menu" :size="16" />
        </button>
        <PhDropdown v-if="mobileMenuOpen">
          <PhDropdownItem
            v-for="link in allowed"
            :key="link.id"
            :active="isActive(link.id)"
            @click="onMobileNavClick(link.id)"
          >
            {{ link.label }}
            <span v-if="link.id === 'clients' && unreadTotal > 0" class="ph-topnav__badge">{{
              unreadTotal > 99 ? '99+' : unreadTotal
            }}</span>
          </PhDropdownItem>
          <template v-if="hasBothRoles">
            <div class="ph-topnav__divider" />
            <PhDropdownItem :active="userStore.panelRole === 'ADMIN'" @click="onMobileRoleClick('ADMIN')">
              {{ $t('nav.adminPanel') }}
            </PhDropdownItem>
            <PhDropdownItem :active="userStore.panelRole === 'COACH'" @click="onMobileRoleClick('COACH')">
              {{ $t('nav.coachPanel') }}
            </PhDropdownItem>
          </template>
        </PhDropdown>
      </div>

      <!-- Logo -->
      <RouterLink :to="{ name: 'dashboard' }" class="ph-topnav__logo">
        <div class="ph-topnav__logo-mark">
          <PhMark :size="16" />
        </div>
        <span class="ph-topnav__brand">Progress Hub</span>
        <span class="ph-topnav__tag">{{ roleTagLabel }}</span>
      </RouterLink>

      <!-- Primary nav -->
      <nav class="ph-topnav__nav">
        <button
          v-for="link in primary"
          :key="link.id"
          class="ph-topnav__nav-btn"
          :class="{ 'ph-topnav__nav-btn--active': isActive(link.id) }"
          @click="navigate(link.id)"
        >
          {{ link.label }}
          <span
            v-if="link.id === 'clients' && unreadTotal > 0"
            class="ph-topnav__badge"
            :title="$t('nav.unreadMessages', { count: unreadTotal })"
            >{{ unreadTotal > 99 ? '99+' : unreadTotal }}</span
          >
        </button>

        <div v-if="secondary.length" ref="moreRef" class="ph-topnav__more">
          <button
            class="ph-topnav__nav-btn"
            :class="{ 'ph-topnav__nav-btn--active': moreActive }"
            @click.stop="moreOpen = !moreOpen"
          >
            {{ $t('nav.more') }}
            <PhIcon name="chevronDown" :size="12" />
          </button>
          <PhDropdown v-if="moreOpen">
            <PhDropdownItem v-for="s in secondary" :key="s.id" :active="isActive(s.id)" @click="onMoreClick(s.id)">
              <PhIcon :name="s.icon || 'reports'" :size="14" />
              {{ s.label }}
            </PhDropdownItem>
          </PhDropdown>
        </div>
      </nav>

      <!-- ⌘K trigger -->
      <button class="ph-topnav__search" @click="cmdOpen = true">
        <PhIcon name="search" :size="13" />
        <span class="ph-topnav__search-text">{{ $t('common.search') }}</span>
        <span class="ph-topnav__shortcut num">⌘K</span>
      </button>

      <!-- Theme switcher -->
      <div ref="themeRef" class="ph-topnav__menu">
        <button class="ph-topnav__icon-btn" @click.stop="themeOpen = !themeOpen">
          <PhIcon
            :name="tweaks.themePref === 'dark' ? 'moon' : tweaks.themePref === 'light' ? 'sun' : 'sparkle'"
            :size="15"
          />
        </button>
        <PhDropdown v-if="themeOpen" right>
          <PhDropdownItem
            v-for="o in themeOptions"
            :key="o.v"
            :active="tweaks.themePref === o.v"
            @click="onThemeClick(o.v)"
          >
            <PhIcon :name="o.icon" :size="14" />
            {{ o.l }}
          </PhDropdownItem>
        </PhDropdown>
      </div>

      <!-- Language switcher -->
      <div ref="langRef" class="ph-topnav__menu">
        <button class="ph-topnav__icon-btn" :title="$t('settings.language')" @click.stop="langOpen = !langOpen">
          <PhIcon name="globe" :size="15" />
        </button>
        <PhDropdown v-if="langOpen" right>
          <PhDropdownItem
            v-for="l in locales"
            :key="l.value"
            :active="currentLocale === l.value"
            @click="onLangClick(l.value)"
          >
            {{ l.label }}
          </PhDropdownItem>
        </PhDropdown>
      </div>

      <!-- Role switcher (admin/coach for dual-role users) -->
      <div v-if="hasBothRoles" class="ph-topnav__role">
        <button
          class="ph-topnav__role-btn"
          :class="{ 'ph-topnav__role-btn--active': userStore.panelRole === 'ADMIN' }"
          @click="switchRole('ADMIN')"
        >
          {{ $t('nav.admin') }}
        </button>
        <button
          class="ph-topnav__role-btn"
          :class="{ 'ph-topnav__role-btn--active': userStore.panelRole === 'COACH' }"
          @click="switchRole('COACH')"
        >
          {{ $t('nav.coach') }}
        </button>
      </div>

      <!-- User menu -->
      <div ref="userRef" class="ph-topnav__menu">
        <button class="ph-topnav__user-btn" @click.stop="userOpen = !userOpen">
          <span class="ph-topnav__avatar">{{ userInitials }}</span>
          <PhIcon name="chevronDown" :size="12" />
        </button>
        <PhDropdown v-if="userOpen" right>
          <div class="ph-topnav__user-info">
            <div class="ph-topnav__user-name">{{ userStore.displayName || $t('nav.member') }}</div>
            <div class="ph-topnav__user-email">{{ userStore.email }}</div>
          </div>
          <PhDropdownItem @click="onSettingsClick">
            <PhIcon name="person" :size="14" />
            {{ $t('nav.settings') }}
          </PhDropdownItem>
          <PhDropdownItem @click="toggleDensity">
            <PhIcon name="drag" :size="14" />
            {{ tweaks.density === 'compact' ? $t('nav.densityComfortable') : $t('nav.densityCompact') }}
          </PhDropdownItem>
          <div class="ph-topnav__divider" />
          <PhDropdownItem danger @click="$emit('logout')">
            <PhIcon name="logout" :size="14" />
            {{ $t('nav.signOut') }}
          </PhDropdownItem>
        </PhDropdown>
      </div>
    </div>

    <PhCommandPalette v-model="cmdOpen" :pages="palettePages" @navigate="navigate" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import PhIcon from './PhIcon.vue'
import PhMark from './PhMark.vue'
import PhDropdown from './PhDropdown.vue'
import PhDropdownItem from './PhDropdownItem.vue'
import PhCommandPalette from './PhCommandPalette.vue'
import { useUserStore } from '@/stores/user-store'
import { useDesignSystem, type ThemePref } from '@/composables/useDesignSystem'
import { useLanguage } from '@/composables/useLanguage'
import { useUnreadMessages } from '@/composables/useUnreadMessages'
import type { AppLocale } from '@/i18n'

defineEmits<{ (e: 'logout'): void }>()

const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()
const { total: unreadTotal } = useUnreadMessages()
const { tweaks, setTweak } = useDesignSystem()
const { currentLocale, locales, setLocale } = useLanguage()

const userOpen = ref(false)
const themeOpen = ref(false)
const langOpen = ref(false)
const moreOpen = ref(false)
const mobileMenuOpen = ref(false)
const cmdOpen = ref(false)

const userRef = ref<HTMLElement | null>(null)
const themeRef = ref<HTMLElement | null>(null)
const langRef = ref<HTMLElement | null>(null)
const moreRef = ref<HTMLElement | null>(null)
const mobileMenuRef = ref<HTMLElement | null>(null)

const hasBothRoles = computed(() => userStore.roles.includes('ADMIN') && userStore.roles.includes('COACH'))

const isAdminPanel = computed(() => userStore.panelRole === 'ADMIN')

type NavLink = { id: string; label: string; icon?: string; adminOnly?: boolean }

// Order matters twice over: it is the reading order, and only the first six allowed links
// stay on the bar (see `primary` below) - the rest fall into the overflow menu.
//
// Coaches sits high for admins on purpose. An admin's job is mostly about coaches, and
// coaches are where billing now lives (#238); it used to be seventh, which put it in the
// overflow. Adding an admin-only link above it pushes something off the bar, so check
// `primary` before doing that.
//
// A COACH sees this list with the adminOnly entries filtered out, and their remaining order
// (dashboard, clients, reports, schedules, exercise library) is unchanged by this.
const allLinks = computed<NavLink[]>(() => [
  { id: 'dashboard', label: t('nav.dashboard') },
  { id: 'coaches', label: t('nav.coaches'), adminOnly: true },
  { id: 'clients', label: t('nav.clients') },
  { id: 'members', label: t('nav.members'), adminOnly: true },
  { id: 'reports', label: t('nav.reports') },
  { id: 'report-schedules', label: t('nav.schedules') },
  { id: 'exercise-library', label: t('nav.exerciseLibrary'), icon: 'dumbbell' },
  { id: 'translations', label: t('nav.translations'), icon: 'globe', adminOnly: true },
  { id: 'food-database', label: t('nav.foodDatabase'), icon: 'meal', adminOnly: true },
  { id: 'exercise-database', label: t('nav.exerciseDatabase'), icon: 'dumbbell', adminOnly: true },
])

const allowed = computed(() => allLinks.value.filter((l) => !l.adminOnly || isAdminPanel.value))

const primary = computed(() => allowed.value.slice(0, 6))
const secondary = computed(() => allowed.value.slice(6))

const moreActive = computed(() => secondary.value.some((s) => isActive(s.id)))

// Settings is reachable by admins and coaches alike — coaches set their vacation there.
const palettePages = computed(() => [...allowed.value, { id: 'settings', label: t('nav.settings'), icon: 'person' }])

const roleTagLabel = computed(() => (isAdminPanel.value ? t('nav.admin') : t('nav.coach')))

const userInitials = computed(() => {
  const name = userStore.displayName || userStore.email || '?'
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
})

const themeOptions = computed<{ v: ThemePref; l: string; icon: string }[]>(() => [
  { v: 'system', l: t('nav.themeSystem'), icon: 'sparkle' },
  { v: 'light', l: t('nav.themeLight'), icon: 'sun' },
  { v: 'dark', l: t('nav.themeDark'), icon: 'moon' },
])

function isActive(id: string): boolean {
  const cur = router.currentRoute.value.name
  if (cur === id) return true
  if (id === 'clients' && cur === 'client-detail') return true
  if (id === 'reports' && cur === 'report-detail') return true
  return false
}

function navigate(id: string) {
  router.push({ name: id }).catch(() => {})
}

function onMoreClick(id: string) {
  navigate(id)
  moreOpen.value = false
}

function onMobileNavClick(id: string) {
  navigate(id)
  mobileMenuOpen.value = false
}

function onMobileRoleClick(role: 'ADMIN' | 'COACH') {
  switchRole(role)
  mobileMenuOpen.value = false
}

function onThemeClick(v: ThemePref) {
  setTweak('themePref', v)
  themeOpen.value = false
}

function onLangClick(v: AppLocale) {
  setLocale(v)
  langOpen.value = false
}

function onSettingsClick() {
  navigate('settings')
  userOpen.value = false
}

function toggleDensity() {
  setTweak('density', tweaks.value.density === 'compact' ? 'comfortable' : 'compact')
  userOpen.value = false
}

function switchRole(role: 'ADMIN' | 'COACH') {
  userStore.setPanelRole(role)
  router.push({ name: 'dashboard' }).catch(() => {})
}

function onDocClick(e: MouseEvent) {
  const t = e.target as Node
  if (userRef.value && !userRef.value.contains(t)) userOpen.value = false
  if (themeRef.value && !themeRef.value.contains(t)) themeOpen.value = false
  if (langRef.value && !langRef.value.contains(t)) langOpen.value = false
  if (moreRef.value && !moreRef.value.contains(t)) moreOpen.value = false
  if (mobileMenuRef.value && !mobileMenuRef.value.contains(t)) mobileMenuOpen.value = false
}

function onKeydown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    cmdOpen.value = true
  }
  if (e.key === 'Escape') cmdOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
  window.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick)
  window.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped>
.ph-topnav {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--ph-tab-bar);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid var(--ph-border);
}
.ph-topnav__inner {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 28px;
  height: 60px;
  display: flex;
  align-items: center;
  gap: 24px;
}
.ph-topnav__logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  flex-shrink: 0;
}
.ph-topnav__logo-mark {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--ph-accent), var(--ph-accent-dark));
  display: grid;
  place-items: center;
  color: #fff;
  box-shadow: 0 6px 16px -6px var(--ph-accent);
}
.ph-topnav__brand {
  font-size: 15px;
  font-weight: 600;
  color: var(--ph-text);
  letter-spacing: -0.3px;
}
.ph-topnav__tag {
  font-size: 10px;
  font-weight: 600;
  color: var(--ph-text-3);
  padding: 2px 6px;
  background: var(--ph-chip-bg);
  border-radius: 4px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.ph-topnav__nav {
  display: flex;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.ph-topnav__nav-btn {
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--ph-text-2);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  transition:
    color 120ms ease,
    background 120ms ease;
}
.ph-topnav__badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 4px;
  border-radius: 99px;
  background: var(--ph-accent);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
}
.ph-topnav__nav-btn:hover {
  color: var(--ph-text);
}
.ph-topnav__nav-btn--active {
  background: var(--ph-chip-bg);
  color: var(--ph-text);
  font-weight: 600;
}
.ph-topnav__more {
  position: relative;
}
.ph-topnav__search {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 10px 0 12px;
  background: var(--ph-chip-bg);
  border: 1px solid var(--ph-chip-border);
  border-radius: 8px;
  color: var(--ph-text-3);
  cursor: pointer;
  font-family: 'Geist', sans-serif;
}
.ph-topnav__search-text {
  font-size: 12.5px;
  color: var(--ph-text-2);
}
.ph-topnav__shortcut {
  margin-left: 4px;
  font-size: 10.5px;
  font-weight: 600;
  color: var(--ph-text-3);
  padding: 1px 5px;
  background: var(--ph-surface);
  border: 1px solid var(--ph-border);
  border-radius: 4px;
}
.ph-topnav__menu {
  position: relative;
}
.ph-topnav__icon-btn {
  width: 34px;
  height: 34px;
  border: 1px solid var(--ph-chip-border);
  border-radius: 8px;
  background: var(--ph-chip-bg);
  color: var(--ph-text);
  display: grid;
  place-items: center;
  cursor: pointer;
}
.ph-topnav__role {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: 8px;
  background: var(--ph-chip-bg);
  border: 1px solid var(--ph-chip-border);
}
.ph-topnav__role-btn {
  height: 26px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--ph-text-2);
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
}
.ph-topnav__role-btn--active {
  background: var(--ph-surface);
  color: var(--ph-text);
  box-shadow: var(--ph-shadow-sm);
}
.ph-topnav__user-btn {
  height: 34px;
  padding: 0 6px 0 4px;
  border: none;
  border-radius: 99px;
  background: var(--ph-chip-bg);
  color: var(--ph-text);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ph-topnav__avatar {
  width: 26px;
  height: 26px;
  border-radius: 99px;
  background: var(--ph-accent-soft);
  color: var(--ph-accent-dark);
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 700;
}
:root[data-theme='dark'] .ph-topnav__avatar {
  color: var(--ph-accent);
}
.ph-topnav__user-info {
  padding: 8px 10px;
  border-bottom: 1px solid var(--ph-border);
}
.ph-topnav__user-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ph-text);
}
.ph-topnav__user-email {
  font-size: 11px;
  color: var(--ph-text-3);
}
.ph-topnav__divider {
  border-top: 1px solid var(--ph-border);
  margin: 4px 0;
}
.ph-topnav__mobile-menu {
  position: relative;
  display: none;
}

@media (max-width: 1099px) {
  .ph-topnav__mobile-menu {
    display: block;
  }
  .ph-topnav__nav {
    display: none;
  }
  .ph-topnav__role {
    display: none;
  }
  .ph-topnav__search {
    margin-left: auto;
  }
}

@media (max-width: 767px) {
  .ph-topnav__inner {
    padding: 0 14px;
    gap: 10px;
  }
  .ph-topnav__brand {
    display: none;
  }
  .ph-topnav__search {
    width: 34px;
    padding: 0;
    justify-content: center;
    gap: 0;
  }
  .ph-topnav__search-text,
  .ph-topnav__shortcut {
    display: none;
  }
}
</style>
